import React, { createContext, useContext, useState, useEffect } from 'react';

const TeachingModeContext = createContext();

export const useTeachingMode = () => {
  const context = useContext(TeachingModeContext);
  if (!context) {
    throw new Error('useTeachingMode must be used within a TeachingModeProvider');
  }
  return context;
};

export const TeachingModeProvider = ({ children }) => {
  const [mode, setMode] = useState('standard'); // standard, assist, professional
  const [currentWorksheet, setCurrentWorksheet] = useState(null);
  const [worksheetProgress, setWorksheetProgress] = useState({});
  const [showTeachingAids, setShowTeachingAids] = useState(false);
  const [hasUserToggledAids, setHasUserToggledAids] = useState(false); // Track if user has manually controlled the toggle

  // Load saved mode from localStorage
  useEffect(() => {
    const savedMode = localStorage.getItem('teachingMode');
    const savedShowTeachingAids = localStorage.getItem('showTeachingAids');
    const savedHasUserToggledAids = localStorage.getItem('hasUserToggledAids');
    
    if (savedMode && ['standard', 'assist', 'professional'].includes(savedMode)) {
      setMode(savedMode);
    }
    
    // Load teaching aids visibility preference
    if (savedShowTeachingAids !== null) {
      setShowTeachingAids(savedShowTeachingAids === 'true');
    }
    
    // Load user toggle preference
    if (savedHasUserToggledAids !== null) {
      setHasUserToggledAids(savedHasUserToggledAids === 'true');
    }
  }, []);

  // Save mode to localStorage and handle auto-enable only if user hasn't manually controlled it
  useEffect(() => {
    localStorage.setItem('teachingMode', mode);
    
    // Only auto-enable teaching aids when switching to assist mode if user hasn't manually controlled it
    if (mode === 'assist' && !hasUserToggledAids && !showTeachingAids) {
      setShowTeachingAids(true);
    }
  }, [mode, showTeachingAids, hasUserToggledAids]);

  // Save teaching aids visibility preference
  useEffect(() => {
    localStorage.setItem('showTeachingAids', showTeachingAids.toString());
  }, [showTeachingAids]);

  // Save user toggle preference
  useEffect(() => {
    localStorage.setItem('hasUserToggledAids', hasUserToggledAids.toString());
  }, [hasUserToggledAids]);

  const switchMode = (newMode) => {
    setMode(newMode);
    if (newMode === 'standard') {
      setCurrentWorksheet(null);
      // Don't force disable teaching aids - let user control it completely
    }
  };

  const toggleTeachingAids = () => {
    // Mark that user has manually controlled the toggle
    setHasUserToggledAids(true);
    // Toggle the teaching aids visibility - this overrides any mode-based behavior
    setShowTeachingAids(!showTeachingAids);
  };

  const startWorksheet = (worksheetId) => {
    setCurrentWorksheet(worksheetId);
    if (!worksheetProgress[worksheetId]) {
      setWorksheetProgress(prev => ({
        ...prev,
        [worksheetId]: {
          started: new Date().toISOString(),
          completed: false,
          currentStep: 0,
          steps: []
        }
      }));
    }
  };

  const updateWorksheetProgress = (worksheetId, stepIndex, completed = false) => {
    setWorksheetProgress(prev => ({
      ...prev,
      [worksheetId]: {
        ...prev[worksheetId],
        currentStep: stepIndex,
        completed,
        lastUpdated: new Date().toISOString()
      }
    }));
  };

  const completeWorksheet = (worksheetId) => {
    setWorksheetProgress(prev => ({
      ...prev,
      [worksheetId]: {
        ...prev[worksheetId],
        completed: true,
        completedAt: new Date().toISOString()
      }
    }));
  };

  const value = {
    mode,
    switchMode,
    currentWorksheet,
    setCurrentWorksheet,
    worksheetProgress,
    showTeachingAids,
    toggleTeachingAids,
    hasUserToggledAids,
    startWorksheet,
    updateWorksheetProgress,
    completeWorksheet,
    isStandardMode: mode === 'standard',
    isAssistMode: mode === 'assist',
    isProfessionalMode: mode === 'professional'
  };

  return (
    <TeachingModeContext.Provider value={value}>
      {children}
    </TeachingModeContext.Provider>
  );
}; 